import { IconButton } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RadioButtonUnchecked, RadioButtonChecked, Close, Search } from "@mui/icons-material";

import Box from "../../Box";
import Avatar from "../../Avatar";
import FormField from "../../FormField";
import Typography from "../../Typography";
import Helpers from "../../../commons/helpers";
import useStrings from "../../../hooks/useStrings";
import Resources from "../../../constants/resources";
import ControlLabelCheckBox from "../../ControlLabelCheckBox";

import { IManageUser } from "../../../commons/interfaces";
import { ItemType, avatarAndName, itemBox, typography } from "../styles";

interface IProps {
    data: IManageUser[];
    selectedData: IManageUser[];
    onSelected: (data: IManageUser[]) => void;
}

const AddManageUserTab: React.FC<IProps> = ({ onSelected, selectedData, ...props }: IProps) => {
    const strings = useStrings();
    const [search, setSearch] = useState<string>("");
    const [checked, setChecked] = useState<boolean>(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [data, setData] = useState<IManageUser[]>([]);

    const filteredData = useMemo(() => (search ? data.filter((item) => item.fullName.includes(search)) || [] : data), [search, data]);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(selectedData)) {
            setSelected((selectedData || []).map((item) => item?.id));
        }
    }, [selectedData]);

    useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const onSearch = () => {};

    const handleSelectItem = (id: string, selectedItem: IManageUser) => {
        if (selected.includes(id)) {
            setSelected(selected.filter((item) => item !== id));
            onSelected(selectedData.filter((item) => item.id !== selectedItem.id));
        } else {
            setSelected((prev) => [...prev, id]);
            onSelected([...selectedData, selectedItem]);
        }
    };

    const handleChecked = (value: boolean) => {
        setChecked(value);
        setSelected(value === true ? filteredData.map((item) => item.id) : []);
        onSelected(value === true ? data : []);
    };

    const renderItem = useCallback(
        (item: IManageUser) => {
            let isSelected = selected.includes(item.id);
            return (
                <Box
                    key={item.id}
                    sx={(theme) => itemBox(theme, { isSelected, type: ItemType.User })}
                    onClick={() => handleSelectItem(item.id, item)}
                >
                    <Box sx={avatarAndName}>
                        <Avatar size="sm" text={item.fullName} src={item.avatarUrl} style={{ border: "1px #c3c3c3 solid" }} />
                        <Box>
                            <Typography
                                sx={(theme: Theme) =>
                                    typography(theme, {
                                        fontSize: "0.8rem",
                                        fontWeight: "bold",
                                    })
                                }
                            >
                                {item.fullName}
                            </Typography>
                            <Typography sx={typography}>{item.email}</Typography>
                        </Box>
                    </Box>
                    <Box>
                        {isSelected ? (
                            <RadioButtonChecked color="info" sx={{ fontSize: "1rem" }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: "1rem" }} />
                        )}
                    </Box>
                </Box>
            );
        },
        [handleSelectItem, selected]
    );

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-end",
                }}
            >
                <FormField
                    fullWidth
                    size="small"
                    defaultValue={search || ""}
                    placeholder={`${strings.Common.SEARCH}...`}
                    onBlur={(value: any) => setSearch(value)}
                    onKeyPress={(e: any) => {
                        if (e.key === "Enter") {
                            setSearch(e.target.value);
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <IconButton color="info" onClick={() => onSearch()}>
                                <Search />
                            </IconButton>
                        ),
                        endAdornment: search && (
                            <IconButton
                                color="secondary"
                                onClick={() => {
                                    setSearch(undefined);
                                }}
                            >
                                <Close />
                            </IconButton>
                        ),
                    }}
                />
                <ControlLabelCheckBox value={checked} onChangeValue={(value) => handleChecked(value)} label={strings.Common.ALL} />
            </Box>
            <Box
                sx={{
                    mt: 1,
                    display: "grid",
                    gap: "0.5rem",
                    overflowY: "scroll",
                }}
            >
                {filteredData.map(renderItem)}
            </Box>
        </>
    );
};

export default AddManageUserTab;
